/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Individual history item component for maintenance records.
Displays details of a single maintenance event with timestamp and actions.
*/

import React from "react";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow, isValid } from "date-fns";
import { Database } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { UpdateMaintenanceHistoryDialog } from "@/components/maintenance/UpdateMaintenanceHistoryDialog";
import { useNavigate } from "react-router-dom";

type MaintenanceStatus = Database["public"]["Enums"]["maintenance_status"];

interface MaintenanceHistoryItemProps {
  item: {
    id: string;
    issue_id: string;
    bike_id: string;
    status: MaintenanceStatus;
    notes: string | null;
    changed_by: { username: string } | null;
    bike: { serial_number: string } | null;
    change_timestamp: string | null;
    cost?: number | null;
  };
  onUpdate?: () => void;
}

export function MaintenanceHistoryItem({ item, onUpdate }: MaintenanceHistoryItemProps) {
  const navigate = useNavigate();

  const getBadgeColor = (status: MaintenanceStatus) => {
    switch (status) {
      case "reported":
        return "bg-yellow-100 text-yellow-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "broken":
        return "bg-red-100 text-red-800";
      case "ok":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const timeAgo = item.change_timestamp 
    ? (() => {
        const date = new Date(item.change_timestamp);
        return isValid(date) && date.getFullYear() > 1970 
          ? formatDistanceToNow(date, { addSuffix: true })
          : "Unknown time";
      })()
    : "Unknown time";

  return (
    <div className="border-b p-4 last:border-0">
      <div className="flex justify-between items-center gap-2">
        <div>
          <h3 className="font-medium">Bike: {item.bike?.serial_number}</h3>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            Changed by: {item.changed_by?.username}
            {item.cost !== null && item.cost !== undefined && (
              <>
                <span className="mx-1">·</span>
                <span className="text-green-700 font-medium">Cost: £{item.cost}</span>
              </>
            )}
          </p>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium capitalize ${getBadgeColor(item.status)}`}>
            {item.status}
          </span>
          <p className="text-sm text-muted-foreground">
            {timeAgo}
          </p>
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{item.notes}</p>
      <div className="flex gap-2 mt-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(`/maintenance/${item.issue_id}`)}
        >
          View issue history
        </Button>
      </div>
    </div>
  );
}
